import React, { useState, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { ActionButton, Input } from "../components/shared";
import { Icons } from "../components/shared/Icons";
import { authService, publicApi } from "../services";
import { Company } from "../types";
import carBackground from "../assets/images/car_background.jpg";
import carMobileBackground from "../assets/images/car_mobile_background.jpg";
import barbershopBackground from "../assets/images/barbershop_background.jpg";
import barbershopMobileBackground from "../assets/images/barbershop_mobile_background.jpg";
import logo from "../assets/images/logo.png";

interface LoginFormData {
  email: string;
  password: string;
}

const BLUE_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundImage: "linear-gradient(160deg, #38bdf8 0%, #0284c7 42%, #0c4a6e 100%)",
};

const BACKGROUND_LAYER =
  "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500";

function useIsMobileViewport(breakpointPx = 640): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${breakpointPx - 1}px)`).matches
      : true
  );

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [breakpointPx]);

  return isMobile;
}

export const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [backgroundReady, setBackgroundReady] = useState(false);
  const isMobile = useIsMobileViewport();

  useEffect(() => {
    const savedEmail = localStorage.getItem("remembered_email");
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await publicApi.getCompany();
        if (response.success && response.data) {
          setCompany(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch company for login background:", error);
      }
    };

    fetchCompany();
  }, []);

  const isCarCompany = company?.category === "car";
  const isBarbershop = company?.category === "barbershop";

  const backgroundSrc = useMemo(() => {
    if (isCarCompany) {
      return isMobile ? carMobileBackground : carBackground;
    }
    if (isBarbershop) {
      return isMobile ? barbershopMobileBackground : barbershopBackground;
    }
    return null;
  }, [isCarCompany, isBarbershop, isMobile]);

  useEffect(() => {
    if (!backgroundSrc) {
      setBackgroundReady(false);
      return;
    }

    let cancelled = false;
    setBackgroundReady(false);

    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (!cancelled) setBackgroundReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setBackgroundReady(false);
    };
    img.src = backgroundSrc;

    return () => {
      cancelled = true;
    };
  }, [backgroundSrc]);

  if (authService.isAuthenticated()) {
    return <Navigate to="/admin/daily-cash" replace />;
  }

  const currentYear = new Date().getFullYear();
  const companyLogo = company?.logo?.trim() || "";
  const logoSrc = companyLogo || (isCarCompany ? logo : "");
  const copy = isCarCompany
    ? {
        title: company?.name ? `Bem-vindo à ${company.name}` : "Bem-vindo à oficina",
        subtitle: "Acesse o painel para gerenciar veículos, agendamentos e o caixa",
        submit: "Entrar no painel",
        submitting: "Entrando...",
        loginFailed: "Não foi possível entrar. Confira o e-mail e a senha e tente novamente.",
        footer: `© ${currentYear} Sistema de gestão para empresas de estética automotiva. Todos os direitos reservados.`,
      }
    : {
        title: company?.name ? `Bem-vindo à ${company.name}` : "Seja bem-vindo!",
        subtitle: "Entre na sua conta para continuar",
        submit: "Entrar",
        submitting: "Entrando...",
        loginFailed: "Não foi possível entrar. Confira o e-mail e a senha e tente novamente.",
        footer: `© ${currentYear} Sistema de Agendamentos. Todos os direitos reservados.`,
      };

  const handleAdminLogin = async (loginData: LoginFormData): Promise<void> => {
    try {
      const response = await authService.login(loginData);

      if (response.success && response.data) {
        // Save email if remember me is checked
        if (rememberMe) {
          localStorage.setItem("remembered_email", loginData.email);
        } else {
          localStorage.removeItem("remembered_email");
        }

        // Redirect to admin dashboard
        window.location.href = `/admin/daily-cash`;
      } else {
        throw new Error(response.error || copy.loginFailed);
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Senha é obrigatória";
    } else if (formData.password.length < 4) {
      newErrors.password = "Senha deve ter pelo menos 4 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    try {
      await handleAdminLogin(formData);
    } catch (error: any) {
      setLoginError(error.message || copy.loginFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={BLUE_BACKGROUND_STYLE}
    >
      {backgroundSrc ? (
        <div
          className={`${BACKGROUND_LAYER} ${
            backgroundReady ? "opacity-100" : "opacity-0"
          }`}
          style={{ backgroundImage: `url(${backgroundSrc})` }}
          aria-hidden
        />
      ) : null}

      {/* Login Card */}
      <div className="relative w-full max-w-md z-10">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-800 rounded-3xl blur opacity-20 animate-pulse"></div>

        <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-600/30 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-6 text-center">
            <div className="flex items-center justify-center mx-auto mb-4 h-16">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={company?.name ? `${company.name} logo` : "Logo"}
                  className="h-16 w-auto max-w-[12rem] object-contain"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-700/80 text-white text-2xl font-semibold flex items-center justify-center">
                  {(company?.name || "A").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {copy.title}
            </h1>
            <p className="text-gray-300">{copy.subtitle}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6 bg-gray-800">
            <Input
              id="login-email"
              type="email"
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="seu@email.com"
              autoComplete="email"
              variant="onDark"
              error={errors.email}
              leadingIcon={<Icons.Person sx={{ fontSize: 20 }} />}
            />

            <Input
              id="login-password"
              type="password"
              label="Senha"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              variant="onDark"
              error={errors.password}
              leadingIcon={<Icons.Lock sx={{ fontSize: 20 }} />}
            />

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-gray-600 bg-gray-700 border-gray-600 rounded focus:ring-gray-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-300">
                  Lembrar de mim
                </span>
              </label>
              <button
                type="button"
                className="text-sm text-gray-400 hover:text-gray-200 font-medium transition-colors duration-200"
              >
                Esqueci a senha
              </button>
            </div>

            {/* Login Error */}
            {loginError && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-4">
                <p className="text-red-200 text-sm flex items-center gap-2">
                  <span>⚠️</span>
                  {loginError}
                </p>
              </div>
            )}

            {/* Login Button */}
            <ActionButton
              type="submit"
              variant="primary"
              size="lg"
              loading={isSubmitting}
              className="w-full py-4 font-bold text-lg justify-center"
            >
              {!isSubmitting && (
                <div className="flex items-center justify-center gap-3">
                  <span>{copy.submit}</span>
                </div>
              )}
              {isSubmitting && copy.submitting}
            </ActionButton>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-gray-400 text-sm">
        <p>{copy.footer}</p>
      </div>
    </div>
  );
};
